import { z } from "zod";
import { type ILanguage, LANGUAGES, LANGUAGE_VALUES } from "@/types";

function coerceString(value: unknown): string | undefined {
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed ? trimmed : undefined;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (Array.isArray(value)) {
        const first = value.find((v) => typeof v === "string" || typeof v === "number" || typeof v === "boolean");
        return first === undefined ? undefined : coerceString(first);
    }
    return undefined;
}

function coerceNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) {
            return undefined;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    if (Array.isArray(value)) {
        const first = value.find((v) => typeof v === "number" || typeof v === "string");
        return first === undefined ? undefined : coerceNumber(first);
    }
    return undefined;
}

function coerceStringList(value: unknown): string[] | undefined {
    // Undefined means param missing (use default). Empty list means param present but empty/invalid.
    if (value === undefined) {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value
            .filter((v): v is string => typeof v === "string")
            .map((s) => s.trim())
            .filter(Boolean);
    }
    const str = coerceString(value);
    if (str === undefined) {
        return [];
    }
    return str
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

function coercePage(value: unknown): number | undefined {
    const num = coerceNumber(value);
    if (num === undefined || !Number.isInteger(num) || num < 1) {
        return undefined;
    }
    return num === 1 ? undefined : num;
}

const stringSchema = z.preprocess(coerceString, z.string().min(1)).optional();
const stringListSchema = z.preprocess(coerceStringList, z.array(z.string())).optional();
const languageEnum = z.enum(LANGUAGE_VALUES);

const idSchema = z
    .preprocess(coerceString, z.string().min(1))
    .transform((value) => value?.toLowerCase())
    .optional();

const languagesSchema = stringListSchema
    .transform((list) => {
        if (!list) {
            return undefined;
        }
        const filtered = list.filter((value): value is ILanguage => languageEnum.safeParse(value).success);
        const deduped = Array.from(new Set(filtered));
        return LANGUAGES.filter((lang) => deduped.includes(lang));
    })
    .optional();

const modulesSchema = stringListSchema
    .transform((list) => {
        if (!list) {
            return undefined;
        }
        return Array.from(new Set(list)).sort();
    })
    .optional();

const pageSchema = z.preprocess(coercePage, z.number().int().positive()).optional();

const versionSearchSchema = z.object({
    id: idSchema,
    q: stringSchema,
    since: stringSchema,
    empty: languagesSchema,
    langs: languagesSchema,
    mods: modulesSchema,
    page: pageSchema,
});

export type VersionSearch = z.infer<typeof versionSearchSchema>;

export function validateVersionSearch(search: Record<string, unknown>): VersionSearch {
    const result = versionSearchSchema.safeParse(search);
    return result.success ? result.data : {};
}

export function activeLanguages(search: VersionSearch): ILanguage[] {
    return search.langs ?? LANGUAGES;
}

export function activeEmptyLanguages(search: VersionSearch): ILanguage[] {
    return search.empty ?? [];
}

export function canonicalSearchKey(search: VersionSearch): string {
    const empty = (search.empty ?? []).slice().sort().join(",");
    const langs = search.langs ? search.langs.slice().sort().join(",") : "*";
    const mods = search.mods ? search.mods.slice().sort().join(",") : "*";
    return [search.id ?? "", search.q ?? "", search.since ?? "", empty, langs, mods].join("|");
}

export function normalizeLangsParam(langs: ILanguage[]): ILanguage[] | undefined {
    const deduped = Array.from(new Set(langs));
    const inOrder = LANGUAGES.filter((l) => deduped.includes(l));
    if (inOrder.length === LANGUAGES.length) {
        return undefined;
    }
    return inOrder;
}

export function normalizeModsParam(mods: string[], allModules: string[]): string[] | undefined {
    const allowed = new Set(allModules);
    const filtered = mods
        .map((m) => m.trim())
        .filter(Boolean)
        .filter((m) => allowed.has(m));
    const deduped = Array.from(new Set(filtered));
    if (deduped.length === allModules.length) {
        return undefined;
    }
    return deduped.sort();
}
